import { Badge, Switch, Text } from '@mantine/core';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useState } from 'react';

import { tariffTypes, currencyFormat } from 'constants/tariffConstants';
import { provinceOptions } from 'constants/provinceConstants';
import { Tariff } from 'interfaces/tariff';
import { StatusBadge } from 'components/common/StatusBadge';
import { formatDateOnly } from 'utils/dateUtils';

type Translate = (key: string, defaultMessage: string) => string;

const tariffTypeColors: Record<string, string> = {
  Base: 'blue',
  'Base Tariff': 'blue',
  Technical: 'teal',
  'Technical Tariff': 'teal',
  Modifier: 'orange',
  'Modifier Tariff': 'orange',
};

const percentFormat = new Intl.NumberFormat(undefined, { style: 'percent' });

const renderCurrency = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Text size="sm" fw={600} ta="right">
      {currencyFormat.format(amount)}
    </Text>
  );
};

const renderRate = (value: unknown, type: string) => {
  if (type === 'Modifier' || type === 'Modifier Tariff') {
    if (value === null || value === undefined || value === '') {
      return renderCurrency(undefined);
    }

    const rate = Number(value);

    return Number.isFinite(rate) ? (
      <Text size="sm" fw={600} ta="right">
        {percentFormat.format(rate)}
      </Text>
    ) : (
      renderCurrency(undefined)
    );
  }

  return renderCurrency(value);
};

export const TariffColumns = (
  translate: Translate = (_key, defaultMessage) => defaultMessage
): MRT_ColumnDef<Tariff>[] => [
  {
    accessorKey: 'code',
    header: 'Code',
    size: 110,
    minSize: 90,
    maxSize: 140,
    mantineEditTextInputProps: {
      required: true,
    },
    Cell: ({ cell }) => (
      <Text size="sm" fw={700} ff="monospace">
        {cell.getValue() as string}
      </Text>
    ),
  },
  {
    accessorKey: 'prefix',
    header: 'Prefix',
    size: 80,
    minSize: 70,
    maxSize: 100,
    mantineEditTextInputProps: {
      required: true,
      type: 'number',
    },
    Cell: ({ cell }) => (
      <Badge variant="light" color="gray" radius="sm">
        {cell.getValue() as number}
      </Badge>
    ),
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 420,
    minSize: 280,
    maxSize: 700,
    mantineEditTextInputProps: {
      required: true,
    },
    Cell: ({ cell }) => (
      <Text
        size="sm"
        lineClamp={2}
        title={(cell.getValue() as string) || undefined}
      >
        {(cell.getValue() as string) || '—'}
      </Text>
    ),
  },
  {
    accessorKey: 'province',
    header: 'Province',
    size: 100,
    minSize: 90,
    maxSize: 120,
    filterVariant: 'select',
    mantineFilterSelectProps: {
      data: provinceOptions,
      clearable: true,
    },
    editVariant: 'select',
    mantineEditSelectProps: {
      data: provinceOptions,
      required: true,
    },
    Cell: ({ cell }) => (
      <Badge variant="light" radius="sm">
        {cell.getValue() as string}
      </Badge>
    ),
  },
  {
    accessorKey: 'specialtyCode',
    header: 'Specialty',
    size: 120,
    minSize: 100,
    maxSize: 160,
    mantineEditTextInputProps: {
      required: true,
    },
    Cell: ({ cell }) => (
      <Text size="sm">{(cell.getValue() as string) || '—'}</Text>
    ),
  },
  {
    accessorKey: 'type',
    header: 'Type',
    size: 150,
    minSize: 130,
    maxSize: 180,
    filterVariant: 'select',
    mantineFilterSelectProps: {
      data: tariffTypes,
      clearable: true,
    },
    editVariant: 'select',
    mantineEditSelectProps: {
      data: tariffTypes,
      required: true,
    },
    Cell: ({ cell }) => {
      const type = cell.getValue() as string;

      return (
        <Badge
          variant="light"
          color={tariffTypeColors[type] ?? 'gray'}
          radius="sm"
          style={{ textTransform: 'none' }}
        >
          {type || 'Unknown'}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'effectiveDate',
    header: 'Effective Date',
    size: 140,
    minSize: 125,
    maxSize: 170,
    mantineEditTextInputProps: {
      required: true,
      type: 'date',
    },
    Cell: ({ cell }) => (
      <Text size="sm">
        {formatDateOnly(cell.getValue() as string, { fallback: '—' })}
      </Text>
    ),
  },
  {
    accessorKey: 'rate',
    header: 'Rate',
    size: 110,
    minSize: 95,
    maxSize: 130,
    mantineEditTextInputProps: {
      required: true,
      inputMode: 'decimal',
    },
    mantineTableHeadCellProps: {
      style: { textAlign: 'right' },
    },
    mantineTableBodyCellProps: {
      style: { textAlign: 'right' },
    },
    Cell: ({ cell, row }) => renderRate(cell.getValue(), row.original.type),
  },
  {
    accessorKey: 'anesthesiaServiceValue',
    header: 'Anesthesia Value',
    size: 140,
    minSize: 125,
    maxSize: 170,
    mantineEditTextInputProps: {
      required: false,
      inputMode: 'decimal',
    },
    mantineTableHeadCellProps: {
      style: { textAlign: 'right' },
    },
    mantineTableBodyCellProps: {
      style: { textAlign: 'right' },
    },
    Cell: ({ cell }) => renderCurrency(cell.getValue()),
  },
  {
    accessorKey: 'applyLocationModifier',
    header: translate(
      'tariffs.fields.applyLocationModifier',
      'Apply location modifier'
    ),
    size: 180,
    minSize: 160,
    maxSize: 220,
    filterVariant: 'select',
    mantineFilterSelectProps: {
      data: [
        {
          label: translate('tariffs.values.enabled', 'Enabled'),
          value: 'true',
        },
        {
          label: translate('tariffs.values.disabled', 'Disabled'),
          value: 'false',
        },
      ],
      clearable: true,
    },
    Edit: ({ cell }) => {
      const [checked, setChecked] = useState<boolean>(
        (cell.getValue() as boolean | undefined) ?? true
      );

      cell.row._valuesCache.applyLocationModifier = checked;

      return (
        <Switch
          label={translate(
            'tariffs.fields.applyLocationModifier',
            'Apply location modifier'
          )}
          description={translate(
            'tariffs.descriptions.applyLocationModifier',
            'Apply geographic fee differentials when calculating this base tariff'
          )}
          checked={checked}
          data-testid="applyLocationModifierToggle"
          onChange={(event) => {
            const value = event.currentTarget.checked;
            setChecked(value);
            cell.row._valuesCache.applyLocationModifier = value;
          }}
        />
      );
    },
    Cell: ({ cell }) => {
      const shouldApplyLocationModifier =
        (cell.getValue() as boolean | undefined) ?? true;

      return (
        <StatusBadge active={shouldApplyLocationModifier}>
          {shouldApplyLocationModifier
            ? translate('tariffs.values.enabled', 'Enabled')
            : translate('tariffs.values.disabled', 'Disabled')}
        </StatusBadge>
      );
    },
  },
];
